/**
 * i18n bootstrap — react-i18next config.
 *
 * Locale resolution order:
 *  1. localStorage `adjudon_locale` (user's explicit choice via footer switcher)
 *  2. `navigator.language` mapped to supported code
 *  3. Fallback to `en`
 *
 * Auth-pages are the first surface translated. Dashboard/billing/etc.
 * will be migrated in later phases; until then react-i18next falls
 * back to the key itself for missing translations, which is fine
 * because dashboard strings are still in JSX.
 *
 * Per CLAUDE.md Cardinal Rule #3: all translation files are bundled
 * at build time (no runtime fetch to a translation CDN). EU residency
 * preserved.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enAuth from "./locales/en/auth.json";
import deAuth from "./locales/de/auth.json";
import enDashboard from "./locales/en/dashboard.json";
import deDashboard from "./locales/de/dashboard.json";

export const SUPPORTED_LOCALES = ["en", "de"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = "adjudon_locale";

function detectInitialLocale(): SupportedLocale {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "de") return stored;
  } catch {
    // localStorage unavailable (Safari private mode) — fall through
  }
  const navLang = (window.navigator?.language || "en").toLowerCase();
  if (navLang.startsWith("de")) return "de";
  return "en";
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { auth: enAuth, dashboard: enDashboard },
    de: { auth: deAuth, dashboard: deDashboard },
  },
  lng: detectInitialLocale(),
  fallbackLng: "en",
  ns: ["auth", "dashboard"],
  defaultNS: "auth",
  interpolation: {
    // React already escapes by default. Disabling i18next escaping
    // prevents double-escaping when the translation contains HTML
    // entities like &apos;.
    escapeValue: false,
  },
  // Keys missing from a locale fall back to `en` automatically; we
  // also log them in dev so missing translations are obvious.
  saveMissing: false,
  returnEmptyString: false,
});

/** Switch and persist the user's locale. */
export function setLocale(locale: SupportedLocale): void {
  void i18n.changeLanguage(locale);
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore (private-mode Safari)
  }
}

export function getLocale(): SupportedLocale {
  const current = (i18n.language || "en").slice(0, 2);
  return current === "de" ? "de" : "en";
}

export default i18n;
